/**
 * Canonical report typography — shared across Feasibility inline sections
 * and typed-section components (Environmental, Power, etc.).
 */
import React from 'react';
import type { ReportTheme } from '../types/reportTheme';
import {
  REPORT_SECTION_H2_CLASS,
  REPORT_SECTION_HEADING_TEXT_CLASS,
  reportSectionHeadingStyle,
} from './reportSectionHeading';
import { useViewMode, type ViewMode } from '../components/siterag/viewMode';

/**
 * POLISH-1 (followup to #34) — map the render AUDIENCE to the eyebrow/field-label
 * case transform, the SAME `surface → labelTransform` rule
 * `resolveReportPresentation` applies to the 14 hand-inline strips
 * (`surface === 'export' ? 'none' : 'uppercase'`). Customer-facing output
 * (`export`) de-uppercases; every in-app audience keeps the historical uppercase
 * chrome, so the VIEWER is byte-identical to today.
 */
export function labelTransformForViewMode(mode: ViewMode): 'uppercase' | 'none' {
  return mode === 'export' ? 'none' : 'uppercase';
}

/**
 * Field label — meta line above a narrative block.
 *
 * `reportFieldLabelClass(transform)` is the audience-aware form: on `'uppercase'`
 * it is BYTE-IDENTICAL to the historical literal (`text-xs font-bold uppercase
 * tracking-widest text-text-muted`); on `'none'` (export) it routes the case
 * chrome through {@link labelClass} so it sheds `uppercase tracking-widest`.
 *
 * `REPORT_FIELD_LABEL_CLASS` is retained as the VIEW-default literal (=
 * `reportFieldLabelClass('uppercase')`) for the existing const importers + token
 * tests; it stays uppercase so the on-screen chrome is unchanged.
 */
export function reportFieldLabelClass(transform: 'uppercase' | 'none'): string {
  return `text-xs font-bold ${labelClass(transform)} text-text-muted`.replace(/\s+/g, ' ').trim();
}
export const REPORT_FIELD_LABEL_CLASS = reportFieldLabelClass('uppercase');

/**
 * #34 P2 — labelClass: the eyebrow/field-label CASE chrome, derived from the
 * resolved {@link import('./resolveReportPresentation').ReportPresentation.labelTransform}
 * token instead of the hardcoded literal `'uppercase tracking-widest'`.
 *
 * `'uppercase'` returns the EXACT historical fragment (`'uppercase
 * tracking-widest'`), so routing a hand-inline strip through this helper is
 * BYTE-IDENTICAL to leaving the literal in place. `'none'` drops the case
 * transform AND the wide tracking (an uppercased letter-spacing reads wrong on
 * mixed-case text), letting the label keep its source case.
 *
 * This is the single chokepoint STAGE 2's render-engine collapse routes the 14
 * hand-inline uppercase strips through; in STAGE 1 it is defined and exported
 * but the resolver hands every surface `'uppercase'`, so it changes ZERO
 * rendered output. Callers compose it WITH their existing size/weight/colour
 * classes (e.g. ``text-xs font-bold ${labelClass(t)} text-text-secondary``),
 * matching the shape of the strips it replaces.
 *
 * POLISH-1 (followup to #34) — the SHARED constants (`REPORT_FIELD_LABEL_CLASS`,
 * the meta-chip, the NEPA title) are NOW ALSO de-uppercased on EXPORT (William's
 * decision, consistent with the 14 strips + the v1.2.3 direction). Each has an
 * audience-aware builder (`reportFieldLabelClass` / `reportMetaChipBaseClass` /
 * `nepaDeterminationTitleClass`) that routes the case chrome through this helper:
 * `'uppercase'` is byte-identical to the historical literal (the view default,
 * still exported as the `*_CLASS` const), `'none'` (export) sheds the case +
 * tracking. The live consumer (`ReportFieldLabel`) reads the audience via
 * `useViewMode` → `labelTransformForViewMode`, so the VIEWER is unchanged and only
 * the EXPORT PDF/DOCX de-uppercases.
 */
export function labelClass(transform: 'uppercase' | 'none'): string {
  return transform === 'uppercase' ? 'uppercase tracking-widest' : '';
}

/**
 * Body prose inside report sections.
 *
 * PART K1 — the `text-sm` (14px) size here is the SAME unified body baseline
 * that {@link import('./reportSectionHeading').reportTypographySizes}`.body`
 * resolves to at multiplier=1 (`REPORT_TYPE_SCALE[*].bodyRem === 0.875`). The
 * Tailwind literal is retained because every class-string consumer expects
 * it (and the reportSurfaceParity oracle pins it); theme-scaled prose layers
 * the resolver size inline on top (inline wins). One body baseline, two
 * representations.
 */
export const REPORT_BODY_TEXT_CLASS = 'text-sm text-text-secondary leading-relaxed';

/** Optional inner content panel (grid cells, callout boxes). */
export const REPORT_INNER_PANEL_CLASS =
  'rounded-lg border border-border-subtle bg-surface-1 p-4';

/** Section separator chrome on EditorialSectionShell wrappers. */
export const REPORT_INLINE_SECTION_CHROME_CLASS = 'py-8 border-b border-border-subtle';

/**
 * Subtle inline meta chip — border tint only, no saturated fill + text pairing.
 *
 * `reportMetaChipBaseClass(transform)` is the audience-aware form: `'uppercase'`
 * is BYTE-IDENTICAL to the historical literal (note: the chip used `tracking-wide`,
 * which {@link labelClass} sheds along with `uppercase` on `'none'`); `'none'`
 * (export) drops the case + tracking chrome. `REPORT_META_CHIP_BASE_CLASS` is the
 * VIEW-default literal for const importers + token tests.
 */
export function reportMetaChipBaseClass(transform: 'uppercase' | 'none'): string {
  const tracking = transform === 'uppercase' ? 'uppercase tracking-wide' : '';
  return `inline-flex items-center rounded-md border px-2 py-0.5 text-[11px] font-medium ${tracking} leading-snug`
    .replace(/\s+/g, ' ')
    .trim();
}
export const REPORT_META_CHIP_BASE_CLASS = reportMetaChipBaseClass('uppercase');

/**
 * Mitigation-plan effectiveness badge. LLM output may append qualifiers
 * (e.g. "High (Regulatory Requirement)"); tier is inferred from prefix.
 *
 * `transform` (default `'uppercase'`) flows the render audience through the chip's
 * case chrome — the chip helpers are pure (cannot call `useViewMode`), so the
 * caller passes the resolved transform (export → `'none'`). Defaulting to
 * `'uppercase'` keeps every existing call site byte-identical on screen.
 */
export function mitigationEffectivenessChipClass(
  effectiveness: string,
  transform: 'uppercase' | 'none' = 'uppercase',
): string {
  const base = reportMetaChipBaseClass(transform);
  const key = effectiveness.trim().toLowerCase();
  if (key.startsWith('high')) {
    return `${base} border-success/25 bg-surface-2/90 text-text-secondary`;
  }
  if (key.startsWith('medium')) {
    return `${base} border-warning/25 bg-surface-2/90 text-text-secondary`;
  }
  if (key.startsWith('low')) {
    return `${base} border-border-subtle bg-surface-2/90 text-text-muted`;
  }
  return `${base} border-border-subtle bg-surface-2/90 text-text-muted`;
}

/** Permit jurisdiction tier — subtle chip for Federal / State / Local rows. */
export function permitLevelChipClass(
  level: string,
  transform: 'uppercase' | 'none' = 'uppercase',
): string {
  const base = reportMetaChipBaseClass(transform);
  const key = level.trim().toLowerCase();
  if (key === 'federal') {
    return `${base} border-primary/25 bg-surface-2/90 text-text-secondary`;
  }
  if (key === 'state') {
    return `${base} border-warning/25 bg-surface-2/90 text-text-secondary`;
  }
  return `${base} border-border-subtle bg-surface-2/90 text-text-muted`;
}

/** Critical-path affordance — border tint only, no danger fill. */
export function criticalPathChipClass(transform: 'uppercase' | 'none' = 'uppercase'): string {
  return `${reportMetaChipBaseClass(transform)} border-warning/30 bg-surface-2/90 text-text-secondary`;
}

/**
 * Permitting pathway row shell — critical path gets a wider left rule.
 *
 * The rule's COLOUR is NOT baked in here: the app-token `border-l-warning`
 * was a fixed amber/brown that ignored the report theme. The caller now
 * paints the left-rule colour inline via `roleColor(theme, 'accent')` so it
 * tracks the active preset (critical-path is schedule EMPHASIS, decorative —
 * not a status warning). This helper only sets the structural `border-l-4`
 * width; `border-l-transparent` keeps the base border from leaking a hue
 * before the inline accent paints over it.
 */
export function permittingPathwayCardClass(criticalPath: boolean): string {
  const base = 'flex items-start gap-4 rounded-lg p-3 border border-border-subtle bg-surface-1';
  return criticalPath ? `${base} border-l-4 border-l-transparent` : base;
}

/** NEPA/CEQA determination callout — neutral panel with muted left accent by tier. */
export function nepaDeterminationCalloutClass(determination: string): string {
  const base = `${REPORT_INNER_PANEL_CLASS} border-l-4 space-y-1`;
  const key = determination.trim().toLowerCase();
  if (key.includes('exclusion')) return `${base} border-l-success/35`;
  if (key.includes('assessment') || /\bea\b/.test(key)) return `${base} border-l-warning/40`;
  return `${base} border-l-warning/50`;
}

/**
 * NEPA/CEQA determination callout title.
 *
 * `nepaDeterminationTitleClass(transform)` is the audience-aware form:
 * `'uppercase'` is BYTE-IDENTICAL to the historical literal (it preserves the
 * title's own `tracking-wide`, NOT labelClass's `tracking-widest` — the view must
 * not shift); `'none'` (export) drops the case + tracking chrome (== the empty
 * `labelClass('none')` fragment). `NEPA_DETERMINATION_TITLE_CLASS` is the
 * VIEW-default literal for const importers + token tests.
 */
export function nepaDeterminationTitleClass(transform: 'uppercase' | 'none'): string {
  const caseChrome = transform === 'uppercase' ? 'uppercase tracking-wide' : labelClass('none');
  return `m-0 font-semibold ${caseChrome} text-text-primary`.replace(/\s+/g, ' ').trim();
}
export const NEPA_DETERMINATION_TITLE_CLASS = nepaDeterminationTitleClass('uppercase');

export interface ReportSectionHeadingProps {
  theme: Pick<ReportTheme, 'headingColor' | 'headingFont'>;
  children: React.ReactNode;
  className?: string;
}

export function ReportSectionHeading({
  theme,
  children,
  className = '',
}: ReportSectionHeadingProps): React.ReactElement {
  return (
    <h2
      data-heading="true"
      className={`${REPORT_SECTION_H2_CLASS} mb-6 ${className}`.trim()}
      style={reportSectionHeadingStyle(theme)}
    >
      {children}
    </h2>
  );
}

export { REPORT_SECTION_HEADING_TEXT_CLASS };

export interface ReportFieldLabelProps {
  children: React.ReactNode;
  className?: string;
}

export function ReportFieldLabel({ children, className = '' }: ReportFieldLabelProps): React.ReactElement {
  // POLISH-1 — resolve the case chrome from the render AUDIENCE (useViewMode),
  // the same `export → 'none'` rule the 14 hand-inline eyebrow strips follow.
  // The in-app viewer (`editor`/`reader`/`public-share`) keeps the uppercase
  // chrome (byte-identical to today); the customer-facing `export` PDF/DOCX
  // de-uppercases.
  const labelCls = reportFieldLabelClass(labelTransformForViewMode(useViewMode()));
  return (
    <div className={`${labelCls} mb-1 ${className}`.trim()}>
      {children}
    </div>
  );
}

export interface ReportBodyTextProps {
  children: React.ReactNode;
  className?: string;
  as?: 'p' | 'div';
}

export function ReportBodyText({
  children,
  className = '',
  as: Tag = 'p',
}: ReportBodyTextProps): React.ReactElement {
  return <Tag className={`${REPORT_BODY_TEXT_CLASS} m-0 ${className}`.trim()}>{children}</Tag>;
}

export interface ReportFieldBlockProps {
  label: string;
  children: React.ReactNode;
  className?: string;
  panel?: boolean;
}

/** Label + body block used across typed report sections. */
export function ReportFieldBlock({
  label,
  children,
  className = '',
  panel = false,
}: ReportFieldBlockProps): React.ReactElement {
  return (
    <div className={`${panel ? REPORT_INNER_PANEL_CLASS : ''} ${className}`.trim()}>
      <ReportFieldLabel>{label}</ReportFieldLabel>
      <ReportBodyText>{children}</ReportBodyText>
    </div>
  );
}
